import "./sentry";
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import './index.css'
import App from './App.tsx'
import { SW_UPDATE_EVENT, wireSwUpdater } from '@/lib/swUpdate'
import { resolveInitialTheme } from '@/lib/themeDefault'

// Register the service worker in PROMPT mode (see vite.config.ts registerType).
// We DELIBERATELY do not auto-apply the update or reload here: a new SW installs
// and waits, and the open tab keeps running its current build until the user
// navigates or refreshes on their own. This is what stops the "came back to the
// tab and the whole page refreshed / lost my scroll" bug — autoUpdate used to fire
// its own window.location.reload() on SW handoff. A genuinely stale lazy chunk
// after a deploy still self-heals via the vite:preloadError handler below (only on
// a real missing-chunk import, never on a passive tab-return). Do NOT re-introduce
// an auto-reload here.
//
// registerSW's RETURN is the only prompt-mode SKIP_WAITING trigger (E-10, G1
// audit: it used to be discarded, so the banner's reload re-served the old shell
// forever). It is wired to the banner's accept path via wireSwUpdater; the reload
// then fires when the new worker takes control (the plugin's own controlling
// listener) — still strictly downstream of the user's Refresh click.
const updateServiceWorker = registerSW({
  onNeedRefresh() {
    // A new build is waiting in the SW. Never reload the live tab from here —
    // announce it instead so AppLayout's UpdateAvailableBanner can offer a
    // USER-TRIGGERED refresh (F3 hardening: previously a silent no-op, so a
    // long-lived tab could run a stale bundle for days with zero indication).
    window.dispatchEvent(new CustomEvent(SW_UPDATE_EVENT))
  },
  onOfflineReady() {
    // Offline-ready — the SW has precached the app shell + assets.
  },
})
wireSwUpdater(updateServiceWorker)

// Every deploy replaces the hashed /assets chunks. A tab still running the old
// entry then fails its next lazy route import — the SPA rewrite answers the
// missing chunk with index.html ("MIME type text/html" module error) and the
// route renders a black screen until a manual refresh (M3 walk, Step 14).
// Vite fires vite:preloadError for exactly this; reload once to pick up the
// new build. The sessionStorage guard stops a reload loop when reloading
// doesn't heal it (e.g. the new deploy itself is broken).
window.addEventListener('vite:preloadError', (event) => {
  const GUARD_KEY = 'rizeos.preload-reload'
  const now = Date.now()
  let state = { count: 0, ts: now }
  try {
    const raw = sessionStorage.getItem(GUARD_KEY)
    if (raw) state = JSON.parse(raw) as { count: number; ts: number }
  } catch {
    // corrupted guard state — treat as first failure
  }
  if (now - state.ts > 60_000) state = { count: 0, ts: now }
  if (state.count >= 2) return // reloading isn't healing this — let the error surface
  sessionStorage.setItem(GUARD_KEY, JSON.stringify({ count: state.count + 1, ts: state.ts }))
  event.preventDefault()
  window.location.reload()
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,       // 30 s -- fresh enough for real-estate data
      gcTime: 5 * 60_000,      // 5 min garbage-collect window
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Stamp the theme BEFORE the first render. ThemeProvider also sets this, but from an
// effect — i.e. after first paint — so with Light as the default (Laura beta item 18)
// every load would flash the dark palette for a frame before correcting itself. Same
// pure resolver as the provider, so the two can never disagree.
document.documentElement.setAttribute('data-theme', resolveInitialTheme(localStorage))

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
)
